import { useEffect, useRef, useState } from "react";

/** How long a newly arrived card is marked (T12): six seconds. */
export const NEW_TICKET_MARK_MS = 6_000;

/**
 * A FEW SECONDS' MARK ON A NEW CARD (T12, T16; D170) — replaces the FLIP.
 *
 * The board no longer animates cards from lane to lane (the arc and the
 * in-lane slides were motion on every tap, on every device). The one motion
 * left besides "late" is this: a card whose key was ABSENT from the previous
 * snapshot of the same scope carries `data-new` for six seconds. The first
 * snapshot of a scope — the page load, a station switch, a view switch — is
 * the baseline and marks nothing: those cards were there before the screen
 * looked. A key that leaves and comes back (an undo) is marked again only if
 * it was absent from the snapshot just before.
 *
 * `keys` is `null` while the scope's data has not arrived. Reduced motion is
 * the CSS's business (a static outline, no pulse).
 */
export function useNewTicketMark(keys: readonly string[] | null, scope: string): ReadonlySet<string> {
  const previous = useRef<{ scope: string; keys: Set<string> } | null>(null);
  const timers = useRef(new Map<string, number>());
  const [marked, setMarked] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (keys === null) return;
    const last = previous.current;
    const current = new Set(keys);
    previous.current = { scope, keys: current };
    if (!last || last.scope !== scope) {
      // A new scope's first read is its baseline — and nothing from the old
      // scope stays marked.
      timers.current.forEach((timer) => window.clearTimeout(timer));
      timers.current.clear();
      setMarked((existing) => (existing.size === 0 ? existing : new Set()));
      return;
    }
    const fresh = keys.filter((key) => !last.keys.has(key));
    if (fresh.length === 0) return;
    setMarked((existing) => new Set([...existing, ...fresh]));
    for (const key of fresh) {
      const pending = timers.current.get(key);
      if (pending !== undefined) window.clearTimeout(pending);
      timers.current.set(
        key,
        window.setTimeout(() => {
          timers.current.delete(key);
          setMarked((existing) => {
            if (!existing.has(key)) return existing;
            const next = new Set(existing);
            next.delete(key);
            return next;
          });
        }, NEW_TICKET_MARK_MS),
      );
    }
  }, [keys, scope]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return marked;
}
